import { NavLink, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { GetPlanResponse } from '@notion-training/shared';
import { useAuth } from '../lib/auth.js';
import { api } from '../lib/api.js';
import { OPEN_PLAN_MODAL_EVENT } from './plan/PlanSection.js';

const navItem = ({ isActive }: { isActive: boolean }) => ({
  padding: '8px 14px',
  borderRadius: 8,
  fontSize: 12.5,
  fontWeight: 500,
  color: isActive ? 'var(--color-text)' : 'var(--color-text-2)',
  background: isActive ? 'var(--color-bg-elevated)' : 'transparent',
  border: `1px solid ${isActive ? 'var(--color-border)' : 'transparent'}`,
  letterSpacing: '0.02em',
});

export function Topbar() {
  const { state, signOut } = useAuth();
  const initials =
    state.status === 'signed-in'
      ? state.user.name
          .split(' ')
          .map((p) => p[0])
          .filter(Boolean)
          .slice(0, 2)
          .join('')
          .toUpperCase()
      : '';

  return (
    <header
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 50,
        backdropFilter: 'blur(16px) saturate(160%)',
        background: 'rgba(8,9,12,0.78)',
        borderBottom: '1px solid var(--color-border)',
      }}
    >
      <div
        style={{
          maxWidth: 1240,
          margin: '0 auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '14px 32px',
          gap: 18,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 36,
              height: 36,
              borderRadius: 9,
              background: 'var(--color-accent-soft)',
              border: '1px solid rgba(79,233,181,0.32)',
              color: 'var(--color-accent)',
              fontWeight: 700,
              fontSize: 13,
              letterSpacing: '-0.04em',
            }}
          >
            [v]
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
              Notion <span style={{ color: 'var(--color-text-3)', fontWeight: 400 }}>·</span> Vim
              <span className="chip chip-accent" style={{ fontSize: 10, padding: '2px 7px' }}>
                v0.1
              </span>
            </div>
            <div style={{ fontSize: 11.5, color: 'var(--color-text-3)' }}>From Zero to Confident</div>
          </div>
        </div>

        <nav
          style={{
            display: 'flex',
            gap: 4,
            padding: 4,
            background: 'var(--color-bg-card)',
            border: '1px solid var(--color-border)',
            borderRadius: 11,
          }}
        >
          <NavLink to="/" end style={navItem}>
            Home
          </NavLink>
          <NavLink to="/dashboard" style={navItem}>
            Dashboard
          </NavLink>
          <NavLink to="/qa" style={navItem}>
            Q&A
          </NavLink>
        </nav>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <PlanNudge />
          <div className="chip" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 11px' }}>
            <span className="pulse-dot" />
            <span style={{ color: 'var(--color-text-2)', fontSize: 11.5 }}>signed in</span>
          </div>
          {state.status === 'signed-in' && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '5px 10px 5px 5px',
                background: 'var(--color-bg-card)',
                border: '1px solid var(--color-border)',
                borderRadius: 999,
              }}
            >
              <div
                style={{
                  width: 26,
                  height: 26,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #4FE9B5, #34D29C)',
                  color: '#042418',
                  fontSize: 10.5,
                  fontWeight: 600,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {initials}
              </div>
              <div style={{ fontSize: 12 }}>{state.user.name.split(' ')[0]}</div>
              <button
                className="btn btn-tiny"
                style={{ marginLeft: 6 }}
                onClick={() => {
                  void signOut();
                }}
              >
                ↗ Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

function PlanNudge() {
  const navigate = useNavigate();
  const planQuery = useQuery<GetPlanResponse>({
    queryKey: ['plan'],
    queryFn: () => api.get<GetPlanResponse>('/api/plan'),
  });

  if (planQuery.isLoading || planQuery.data?.plan) return null;

  const onClick = () => {
    if (window.location.pathname === '/dashboard') {
      // Already on the dashboard — PlanSection is mounted, dispatch the event.
      window.dispatchEvent(new Event(OPEN_PLAN_MODAL_EVENT));
    } else {
      // Otherwise navigate to /dashboard with a flag — PlanSection's mount
      // effect reads the flag and opens the modal. Avoids an event-timing race.
      navigate('/dashboard?openPlan=1');
    }
  };

  return (
    <button
      onClick={onClick}
      style={{
        background: 'transparent',
        color: 'var(--color-accent)',
        border: '1px solid rgba(79,233,181,0.32)',
        padding: '6px 12px',
        borderRadius: 8,
        fontSize: 12,
        cursor: 'pointer',
        fontWeight: 500,
      }}
    >
      ▸ Build my AI plan
    </button>
  );
}
